'use client';

import { ChipsProps } from '@/components/chips/types';
import { MouseEvent, KeyboardEvent } from 'react';
import { sizeClass, variantClass } from '../chips/types';
import Icon from '@/components/icon/Icon';
import clsx from 'clsx';
import { renameDomProps } from '@/utils/sanitizeDomProps';

export function Chips({
  label,
  variant,
  size = 'medium',
  icon,
  onClick,
  selected = false,
  disabled = false,
  selectionMode = 'multiple',
  border = false,
  classNameLabel,
  className,
  ...rest
}: ChipsProps & { selectionMode?: 'single' | 'multiple' }) {
  const resolvedSizeClass = sizeClass[size as keyof typeof sizeClass];
  const renamedProps = renameDomProps(rest);

  const resolvedVariantClass = selected
    ? (variantClass[`${variant}Selected` as keyof typeof variantClass] ??
      variantClass['selected'])
    : variantClass[variant as keyof typeof variantClass] ||
      variantClass['default'];

  const handleClick = (event: MouseEvent) => {
    if (!disabled && onClick) {
      onClick(event);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !disabled) {
      handleClick(event as unknown as MouseEvent<HTMLDivElement>);
    }
  };

  return (
    <div
      data-component-id="C013"
      role="button"
      aria-pressed={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      className={clsx(
        'smzh-chips flex w-max items-center justify-center gap-1.5 align-middle font-medium',
        'transition-all duration-500 ease-in-out',
        resolvedSizeClass,
        resolvedVariantClass,
        {
          disabled: disabled,
          'border border-solid border-gray-300': border,
        },
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...renamedProps}
    >
      {icon && <Icon {...icon} />}
      <span className={`chip-label ${classNameLabel}`}>{label}</span>
    </div>
  );
}

export default Chips;
